import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Flex, Spinner, useToast, Text } from "@chakra-ui/react";
import CorbanForm from "@/components/CorbanForm";
import { Corban, CorbanApiAccessEnum, CorbanApiCredential, CorbanCreditModalityEnum, CorbanModalityPermission } from "@/models/corban.model";
import { User } from "@/models/user.model";
import { getCorbanApisDetails, getCorbanById, updateCorban, setCorbanSellerAuthorized, createUserApiFGTS, createUserApiConsig } from "@/services/corban.service";
import { getOldestMasterByCorbanId, updateUser } from "@/services/user.service";
import { removeMask } from "@/utils/removeMask";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { updateFinancialSettings, associateFinancialSettings } from "@/services/financialSettings.service";
import { formatPhone } from "@/utils/formatPhone";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { updateCreditType } from "@/services/creditType.service";

export default function EditCorbanPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const navigate = useNavigate();
  const backgroundStyle = useConditionalBackground();
  const [corban, setCorban] = useState<Corban | null>(null);
  const [masterUser, setMasterUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fgtApiAccess, setFgtApiAccess] = useState<CorbanApiCredential | undefined>(undefined)
  const [consigApiAccess, setConsigApiAccess] = useState<CorbanApiCredential | undefined>(undefined)

  useEffect(() => {
    const fetchCorbanData = async () => {
      if (!id) {
        toast({
          title: "Erro",
          description: "ID do Corban não fornecido",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        navigate(-1);
        return;
      }

      try {
        const fetchedCorban = await getCorbanById(Number(id));

        setCorban(fetchedCorban);
        try {
          const oldestMasterUser = await getOldestMasterByCorbanId(Number(id));
          setMasterUser(oldestMasterUser);
        } catch (masterError) {
          console.error("Erro ao buscar usuário master:", masterError);
          toast({
            title: "Aviso",
            description: "Não foi possível carregar o usuário master",
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
        const corbanApisDetails = await getCorbanApisDetails(Number(id));

        const responseAny = corbanApisDetails as any;

        const listToSearch = Array.isArray(responseAny)
          ? responseAny
          : (Array.isArray(responseAny?.data) ? responseAny.data : []);

        const fgts = listToSearch.find((c: { productAccess: CorbanApiAccessEnum; }) => c.productAccess === CorbanApiAccessEnum.FGTS);
        setFgtApiAccess(fgts);

        const consig = listToSearch.find((c: { productAccess: CorbanApiAccessEnum; }) => c.productAccess === CorbanApiAccessEnum.CONSIGNADO);
        setConsigApiAccess(consig);

      } catch (error: any) {
        toast({
          title: "Erro ao carregar dados do Corban",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCorbanData();
  }, [id, toast, navigate]);


  const handleSubmit = async (values: any) => {
    const cleanedCNPJ = removeMask(values.document);
    const cleanedCpf = removeMask(values.masterUserCpf);
    const cleanedPhone = removeMask(values.masterUserPhone);
    const cleanedSellerAuthorized = values.sellerAuthorized ? removeMask(values.sellerAuthorized) : null;

    try {
      const corbanData = {
        id: values.id,
        document: cleanedCNPJ,
        legalName: values.legalName,
        businessName: values.businessName,
        automaticApproval: values.automaticApproval,
        financialSettingsId: values.financialSettingsId,
        creditPromoterId: values.creditPromoterId,
        commission: values.commission,
        products: values.products,
        masterName: `${values.masterUserName} ${values.masterUserLastName}`,
        paymentSplit: values.paymentSplit,
        sellerAuthorized: cleanedSellerAuthorized,
      };

      await updateCorban(id!, corbanData);

      if (cleanedSellerAuthorized) {
        try {
          await setCorbanSellerAuthorized(Number(id!), cleanedSellerAuthorized);
        } catch (error) {
          console.error("Erro ao definir vendedor autorizado:", error);
        }
      }

      if (values.masterUserId) {
        const userData: Partial<User> = {
          id: values.masterUserId,
          name: values.masterUserName,
          lastName: values.masterUserLastName,
          document: cleanedCpf,
          cellphone: cleanedPhone,
          roles: values.masterUserRole,
          creditPromoterId: values.creditPromoterId,
          email: values.masterUserEmail,
          password: values.masterUserPassword,
        };

        await updateUser(userData);

        if (values.financialSettingsId) {
          await updateFinancialSettings(values.financialSettingsId, {
            commercialManagerId: values.commercialManagerId,
          });
        }
      }

      // Associar Financial Settings Consignado ao Corban
      if (values?.financialSettingsConsignadoId) {
        try {
          await associateFinancialSettings({
            financialSettingsId: values.financialSettingsConsignadoId,
            creditType: CorbanCreditModalityEnum.CONSIG_WORKER,
            corbanId: Number(id)
          });
        } catch (error) {
          console.error("Erro ao associar financial settings do consignado:", error);
          toast({
            title: "Aviso",
            description: "Erro ao associar configurações financeiras do consignado",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      }

      // Associar Financial Settings FGTS ao Corban
      if (values?.financialSettingsFgtsId) {
        try {
          await associateFinancialSettings({
            financialSettingsId: values.financialSettingsFgtsId,
            creditType: CorbanCreditModalityEnum.FGTS,
            corbanId: Number(id)
          });
        } catch (error) {
          console.error("Erro ao associar financial settings do FGTS:", error);
          toast({
            title: "Aviso",
            description: "Erro ao associar configurações financeiras do FGTS",
            status: "warning",
            duration: 3000,
            isClosable: true,
          });
        }
      }

      // Atualizar o CreditTypeCorban de acordo com o que foi liberado
      await updateCreditType({
        corbanId: values?.id,
        credit_type: CorbanCreditModalityEnum.FGTS,
        api_enable: values?.isFGTSApiEnable ?? false,
        enable: values?.isFGTSEnable ?? false,
        userCanAccess: values?.isFGTSEnable ? 'all' : ''
      });

      await updateCreditType({
        corbanId: values?.id,
        credit_type: CorbanCreditModalityEnum.CONSIG_WORKER,
        api_enable: values?.isConsigApiEnable ?? false,
        enable: values?.isConsigEnable ?? false,
        userCanAccess: values?.isConsigEnable ? 'all' : ''
      });
      if (values?.fgtsWebhookUrl) {
        const body = {
          webhook_fgts: values?.fgtsWebhookUrl!,
          corban_id: corban?.id!,
          granatech_user_id: values.masterUserId,
          granatech_password_id: values?.masterUserPassword!,
          token: values?.fgtsToken
        }
        await createUserApiFGTS(body)
      }

      if (values?.consigWebhookUrl) {
        const body = {
          webhook_consignado: values?.consigWebhookUrl!,
          corban_id: corban?.id!,
          granatech_user_id: values.masterUserId,
          granatech_password_id: values?.masterUserPassword!,
          token: values?.consigToken
        }
        await createUserApiConsig(body)
      }

      toast({
        title: "Corban atualizado com sucesso",
        description: `Corban ${values.businessName} foi atualizado!`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });


      navigate(-1);
    } catch (error: any) {
      console.error("Erro na atualização do Corban:", error);

      toast({
        title: "Erro ao atualizar Corban",
        description: error.response?.data?.message || error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
        height="40vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Flex direction="column" align="center">
          <Spinner size="xl" mb={4} />
          <Text fontSize="xl" fontWeight="bold">
            Carregando...
          </Text>
        </Flex>
      </Box>
    );
  }

  if (!corban) {
    return (
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
        height="40vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text fontSize="xl" fontWeight="bold">
          Corban de id: {id} não encontrado
        </Text>
      </Box>
    );
  }

  const fgtsPermissionPromoter = corban?.CreditPromoter?.CreditTypeCorbans?.find((p: any) => p.credit_type === CorbanCreditModalityEnum.FGTS);
  const commercialManagerId = corban?.FinancialSettings?.commercialManagerId || corban?.CreditPromoter?.FinancialSettings?.commercialManagerId;

  const consigPermissionPromoter = corban?.CreditPromoter?.CreditTypeCorbans?.find((p: any) => p.credit_type === CorbanCreditModalityEnum.CONSIG_WORKER);
  // const fgtsRef :any=corban?.CreditPromoter || corban
  const fgtsPermission = corban?.CreditTypeCorbans?.find((p: CorbanModalityPermission) => p.credit_type === CorbanCreditModalityEnum.FGTS);

  const consigPermission = corban?.CreditTypeCorbans?.find((p: CorbanModalityPermission) => p.credit_type === CorbanCreditModalityEnum.CONSIG_WORKER);

  // const FinancialSettingsConsignado:any = fgtsRef?.FinancialSettingsCorbansAndCreditPromoter?.find((financialSettings:any) => financialSettings.creditType === CorbanCreditModalityEnum.CONSIG_WORKER)
  // const FinancialSettingsFgts = fgtsRef?.FinancialSettingsCorbansAndCreditPromoter?.find((financialSettings:any) => financialSettings.creditType === CorbanCreditModalityEnum.FGTS)
  // console.log("corban", corban)
  const initialValues = {
    id: corban.id,
    document: documentFormatter(corban.document),
    legalName: corban.legalName,
    businessName: corban.businessName,
    paymentSplit: corban.paymentSplit,
    automaticApproval: corban.automaticApproval ?? false,
    financialSettingsId: corban.financialSettingsId || corban.CreditPromoter?.financialSettingsId,
    // financialSettingsConsignadoId: FinancialSettingsConsignado?.FinancialSettings.id,
    // financialSettingsFgtsId: FinancialSettingsFgts?.FinancialSettings.id,
    FinancialSettings: corban.FinancialSettings || corban.CreditPromoter?.FinancialSettings,
    financialSettingsCorban: corban.FinancialSettings,
    // FinancialSettingsConsignado,
    // FinancialSettingsFgts: FinancialSettingsFgts?.FinancialSettings,

    whiteLabelId: corban.WhiteLabel?.id,
    WhiteLabel: corban.WhiteLabel,
    creditPromoterId: corban.creditPromoterId,
    commission: corban.commission,
    products: corban.products || [],
    consigProducts: corban?.ConsigProductCorbans || [],
    masterUserId: masterUser?.id || "",
    masterUserName: masterUser?.name || "",
    masterUserLastName: masterUser?.lastName || "",
    masterUserCpf: cpfFormatter(masterUser?.document || ""),
    masterUserPhone: formatPhone(masterUser?.cellphone || ""),
    masterUserEmail: masterUser?.email || "",
    masterUserRole: masterUser?.roles || [],
    isFGTSEnablePromoter: fgtsPermissionPromoter?.enable || undefined,
    isFGTSEnable: fgtsPermission?.enable || false,
    isFGTSApiEnable: fgtsPermission?.api_enable || false,
    fgtsApikey: fgtApiAccess?.apiKey || '',
    fgtsWebhookUrl: fgtApiAccess?.webhook || '',
    isConsigEnablePromoter: consigPermissionPromoter?.enable || undefined,
    isConsigEnable: consigPermission?.enable || false,
    isConsigApiEnable: consigPermission?.api_enable || false,
    consigApikey: consigApiAccess?.apiKey || '',
    consigWebhookUrl: consigApiAccess?.webhook || '',
    // colocar aqui o token da api granatech
    // consigToken: undefined,
    // fgtsToken: undefined,
    fgtsToken: fgtApiAccess?.token || '',
    consigToken: consigApiAccess?.token || '',
    isCreateAccessApiGranatechFgts: !fgtApiAccess,
    isCreateAccessApiGranatechConsig: !consigApiAccess,
    creditPromoter: corban.CreditPromoter,
    sellerAuthorized: corban.sellerAuthorizedDocument ? cpfFormatter(corban.sellerAuthorizedDocument) : undefined
  };

  return (
    <CorbanForm
      initialValues={initialValues}

      commercialManagerId={
        commercialManagerId
      }
      onSubmit={handleSubmit}
      isNew={false}
      creditPromoterId={corban.creditPromoterId}
    />
  );
}
